<script setup>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import { getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    visible: {
        type: Boolean,
        required: true,
    },
    changesPreview: {
        type: Object,
        default: () => ({ clientChange: null, siteRows: [] }),
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:visible', 'confirm']);
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="emit('update:visible', $event)"
        :header="$t('Review changes')"
        :modal="true"
        :style="{ width: '780px' }"
    >
        <div v-if="!changesPreview.clientChange && changesPreview.siteRows.length === 0" class="text-surface-500 text-sm py-2">
            {{ $t('No changes detected') }}
        </div>
        <div v-else class="flex flex-col gap-4">
            <!-- Client change -->
            <div v-if="changesPreview.clientChange" class="flex items-center gap-3 p-3 bg-surface-50 border border-surface-200 rounded-lg">
                <span class="font-semibold text-sm">{{ $t('Client') }}:</span>
                <span class="line-through text-surface-400 text-sm">{{ changesPreview.clientChange.oldValue }}</span>
                <i class="pi pi-arrow-right text-surface-400 text-xs"></i>
                <span class="font-semibold text-primary-600 text-sm">{{ changesPreview.clientChange.newValue }}</span>
            </div>
            <!-- Site rows -->
            <DataTable v-if="changesPreview.siteRows.length > 0" :value="changesPreview.siteRows" class="p-datatable-sm" responsiveLayout="scroll">
                <Column field="url" :header="$t('Site')" style="min-width:140px">
                    <template #body="slotProps">
                        <span class="text-sm font-medium">{{ slotProps.data.url }}</span>
                        <Tag v-if="slotProps.data.added" severity="success" class="ml-2 text-xs">{{ $t('Added') }}</Tag>
                        <Tag v-if="slotProps.data.removed" severity="danger" class="ml-2 text-xs">{{ $t('Removed') }}</Tag>
                    </template>
                </Column>
                <Column :header="$t('Seller')" style="min-width:130px">
                    <template #body="slotProps">
                        <template v-if="slotProps.data.sellerChanged">
                            <div class="flex flex-col gap-0.5">
                                <span class="line-through text-surface-400 text-xs">{{ slotProps.data.sellerOld }}</span>
                                <span class="font-semibold text-sm">{{ slotProps.data.sellerNew }}</span>
                            </div>
                        </template>
                        <span v-else class="text-surface-300 text-sm">—</span>
                    </template>
                </Column>
                <Column :header="$t('Type')" style="min-width:120px">
                    <template #body="slotProps">
                        <template v-if="slotProps.data.typeChanged">
                            <div class="flex flex-col gap-0.5">
                                <span class="line-through text-surface-400 text-xs">{{ slotProps.data.typeOld }}</span>
                                <span class="font-semibold text-sm">{{ slotProps.data.typeNew }}</span>
                            </div>
                        </template>
                        <span v-else class="text-surface-300 text-sm">—</span>
                    </template>
                </Column>
                <Column :header="$t('Purchase Value')" style="min-width:130px">
                    <template #body="slotProps">
                        <template v-if="slotProps.data.costChanged">
                            <div class="flex flex-col gap-0.5">
                                <span class="line-through text-surface-400 text-xs">{{ slotProps.data.costOld }}</span>
                                <span class="font-semibold text-sm">{{ slotProps.data.costNew }}</span>
                            </div>
                        </template>
                        <span v-else class="text-surface-300 text-sm">—</span>
                    </template>
                </Column>
                <Column :header="$t('Sale Value')" style="min-width:130px">
                    <template #body="slotProps">
                        <template v-if="slotProps.data.saleChanged">
                            <div class="flex flex-col gap-0.5">
                                <span class="line-through text-surface-400 text-xs">{{ slotProps.data.saleOld }}</span>
                                <span class="font-semibold text-sm">{{ slotProps.data.saleNew }}</span>
                            </div>
                        </template>
                        <span v-else class="text-surface-300 text-sm">—</span>
                    </template>
                </Column>
            </DataTable>
        </div>
        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="emit('update:visible', false)" />
            <Button :label="$t('Confirm and save')" icon="pi pi-check" severity="primary" :loading="loading" @click="emit('confirm')" />
        </template>
    </Dialog>
</template>
